import { InputEntryFunctionData } from "@aptos-labs/ts-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { amountToChainUnits } from "@/utils/math";

import { readDexSdk, sdkConfig } from "../dex-sdk";
import { usdcBalanceQueryKey } from "../queries/usdc-balance";
import { useWriteDexSdk } from "../write-dex-sdk-context";
import { useWalletFeePayerWithFallback } from "./wallet-fee-payer";

export function useDepositUsdc() {
  const queryClient = useQueryClient();
  const { subaccountAddress } = useWriteDexSdk();
  const { accountAddress } = useDecibelWallet();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();

  return useMutation({
    mutationFn: async ({
      amount,
      toSubaccountAddress,
    }: {
      amount: number;
      toSubaccountAddress?: string;
    }) => {
      const subaccountAddressToDepositTo = toSubaccountAddress ?? subaccountAddress;
      if (!accountAddress || !subaccountAddressToDepositTo) {
        throw new Error("Wallet not connected");
      }

      const data: InputEntryFunctionData = {
        function: `${sdkConfig.deployment.package}::dex_accounts_entry::deposit_to_subaccount_at`,
        typeArguments: [],
        functionArguments: [
          subaccountAddressToDepositTo,
          sdkConfig.deployment.usdc,
          amountToChainUnits(amount),
        ],
      };

      const pendingTransaction = await signAndSubmitWalletTransaction({ data });
      await readDexSdk.deps.aptos.waitForTransaction({
        transactionHash: pendingTransaction.hash,
      });
    },
    onSuccess: () => {
      if (accountAddress) {
        void queryClient.resetQueries({
          queryKey: usdcBalanceQueryKey({ accountAddr: accountAddress }),
        });
      }
    },
  });
}
